import { useCallback, useMemo, useState } from 'react';
import { CategoryContext } from './CategoryProvider';
import useContext from '../../../../hooks/useContext';
import useOnUpdate from '../../../../hooks/useOnUpdate';
import { StoreContext } from '../../../../providers/store';
import { isEmpty } from '../../../../utils/object';

export interface ILeadtimeOption {
  property: string;
  value: FilterValue;
  label: string;
}

const emptyFilterValues = {} as IFilterValues;

export const defaultLeadtimeOption = {
  property: 'default',
  value: {},
  label: 'Inget val',
} as ILeadtimeOption;

const getInitialLeadtimeFilterOption = (
  filters: IFilters,
  leadtimeFilterOptions: ILeadtimeOption[],
) => {
  const emptyleadtimeFilters = isEmpty(filters.leadtime);
  if (!emptyleadtimeFilters) {
    const firstLeadtimeProperty = Object.keys(filters.leadtime)[0];
    const matchingOption = leadtimeFilterOptions.find(
      ({ property }) => property === firstLeadtimeProperty,
    );

    return matchingOption || defaultLeadtimeOption;
  } else {
    return defaultLeadtimeOption;
  }
};

const mapLeadtimeOptions = (leadtimeFilters: IAvailableFilter[]) => {
  const leadtimeOptions = leadtimeFilters.reduce((leadtimeOptions, filter) => {
    if (filter?.options?.length === 1) {
      leadtimeOptions.push({
        property: filter.property,
        value: { [filter.options[0]]: true },
        label: filter.name,
      });
    }
    return leadtimeOptions;
  }, [] as ILeadtimeOption[]);
  return leadtimeOptions.length > 0 ? [defaultLeadtimeOption, ...leadtimeOptions] : [];
};

export default () => {
  const { userStore } = useContext(StoreContext);
  const { filters, setFilters, availableFilters, updateStoreParam } = useContext(CategoryContext);

  const regularFilters = useMemo(
    () => availableFilters?.filter((filter) => filter.filterSource !== 'leadtime') || [],
    [availableFilters],
  );
  const leadtimeFilters = useMemo(
    () => availableFilters?.filter((filter) => filter.filterSource === 'leadtime') || [],
    [availableFilters],
  );
  const flattendFilters = useMemo(() => {
    return { ...filters.leadtime, ...filters.regular } as IFilterValues;
  }, [filters]);

  const leadtimeFilterOptions = useMemo(
    () => mapLeadtimeOptions(leadtimeFilters),
    [leadtimeFilters],
  );
  const [selectedLeadtimeOption, setSelectedLeadtimeOption] = useState(() =>
    getInitialLeadtimeFilterOption(filters, leadtimeFilterOptions),
  );

  const isSelectedFilter = useCallback(
    (property: string) => {
      return (!!flattendFilters && !!flattendFilters[property]) || false;
    },
    [flattendFilters],
  );
  const selectedFilterValue = useCallback(
    (property: string) => {
      return flattendFilters[property] || {};
    },
    [flattendFilters],
  );

  const updateRegularFilters = useCallback(
    (property: string, value?: FilterValue) => {
      setFilters((prev) => {
        if (!value) {
          const next = { ...prev };
          delete next['regular'][property];
          return next;
        }
        return { ...prev, ['regular']: { ...prev['regular'], [property]: value } };
      });
    },
    [setFilters],
  );

  const updateLeadtimeFilters = useCallback(
    (property: string, value?: FilterValue) => {
      // No option selected => reset leadtime filters
      if (property === 'default') {
        resetLeadtimeFilters();
        return;
      }
      setFilters((prev) => {
        if (!value) {
          const next = { ...prev, leadtime: emptyFilterValues };
          return next;
        }
        return { ...prev, ['leadtime']: { [property]: value } };
      });
    },
    [setFilters, userStore],
  );

  // Leadtime filters are dependant on a selected store. filters.leadtime will never be populated
  // without setting the state for userstore. So when filters are updated and .leadtime is not empty
  // we make sure to update internal state for store searchparameter passed to api request. -> CategoryContext.
  useOnUpdate(
    () => {
      if (!isEmpty(filters.leadtime)) {
        updateStoreParam(userStore?.storeId);
      } else {
        updateStoreParam(undefined);
      }
    },
    undefined,
    [filters],
  );

  const resetRegularFilters = useCallback(() => {
    setFilters((prev) => {
      return { ...prev, regular: emptyFilterValues };
    });
  }, []);

  const resetLeadtimeFilters = useCallback(() => {
    setFilters((prev) => {
      return { ...prev, leadtime: emptyFilterValues };
    });
  }, []);

  return {
    leadtimeFilters,
    regularFilters,
    isSelectedFilter,
    flattendFilters,
    updateRegularFilters,
    updateLeadtimeFilters,
    selectedLeadtimeOption,
    setSelectedLeadtimeOption,
    updateStoreParam,
    leadtimeFilterOptions,
    filters,
    selectedFilterValue,
    resetRegularFilters,
    resetLeadtimeFilters,
  };
};
